/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {GameConfig} from "@nolimitcity/slot-game/bin/core/gameconfig/GameConfig";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {SequentiallyBetWayInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetWayInitialWPController";
import {Timeline, TimelineLite} from "gsap";
import {DragonTribeGameConfig} from "../../DragonTribeGameConfig";
import {DragonTribeGameModuleConfig} from "../../DragonTribeGameModuleConfig";
import {DragonTribeSoundConfig} from "../../DragonTribeSoundConfig";
import {DragonTribeParsedGameData} from "../../server/data/DragonTribeParsedGameData";
import {DragonTribeInitialWinCountUp} from "./DragonTribeInitialWinCountUp";
import {DragonTribeInitialWPView} from "./DragonTribeInitialWPView";
import {WaysWonGFXText} from "./WaysWonGFXText";

export class DragonTribeInitialWPController extends SequentiallyBetWayInitialWPController {
    private _reelsNum:number;
    private _symbolsNum:number;

    private _gameView:DragonTribeInitialWPView;
    private _winCountUp:DragonTribeInitialWinCountUp;
    private _winNumberTimelines:(TimelineLite | null)[] = [];
    private _waysWon:WaysWonGFXText;
    private _winWaysWon:number = 0;
    private _animationTimeline:TimelineLite | null;
    private _animationAborted:boolean = false;

    constructor(indexes:[number, number]) {
        super(indexes, {
            name : "InitialWin",
            betWinMode : BetWinMode.BET_WAY,
            hasWinCountUp : false,
            skippable : true
        });

        this._reelsNum = GameConfig.instance.REELS_NUM;
        this._symbolsNum = GameConfig.instance.SYMBOLS_NUM_IN_REEL[2];

        this._gameView = new DragonTribeInitialWPView();
        this._winCountUp = new DragonTribeInitialWinCountUp({
            font : {
                name : "NumbersWinCountUp",
                size : 20
            },
            tint : parseInt(("#ffffff").replace(/^#/, ''), 16)
        });
        this._winCountUp.position.set(GameConfig.instance.GAME_WIDTH * 0.5, GameConfig.instance.GAME_HEIGHT * 0.5);
        StageManager.getLayer(DragonTribeGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
        this._waysWon = new WaysWonGFXText();
    }

    protected getNextSymbolAnimationTimeOffset(duration:number):number {
        return 0;
    }

    protected addSingleBetWinAnimationToTimeline(betWayWinData:IBetWayWinData, timeline:Timeline, startTime:number):number {
        const delay:number = super.addSingleBetWinAnimationToTimeline(betWayWinData, timeline, startTime);

        // update betWays won
        this._winWaysWon += betWayWinData.numberOfCombinations;
        const winWaysWon = this._winWaysWon;
        timeline.add(() => this._waysWon.showWinWaysWon(winWaysWon), startTime);
        timeline.add(() => {
            // #315
            if (!DragonTribeGameConfig.instance.WIN_BELOW_STAKE) {
                SlotGame.sound.playEffect(`winRow${Number.isInteger(startTime) ? 1 : 2}`);
            }
        }, startTime);
        return delay + 0.8;
    }

    protected addSymbolWinAnimationToTimeline(betWayWinData:IBetWayWinData, timeline:Timeline, startTime:number, symbol:StateReelSymbol, reelId:number, symbolId:number):number {
        const position:[number, number] = Helper.getSymbolPositions(reelId, symbolId, true);
        const fullSize:boolean = (symbol.symName.indexOf("W0") > -1);
        timeline.add(() => this.sortSymbolDepthToTop(symbol), startTime);
        if(!(fullSize && symbolId != 5)) {
            timeline.add(DragonTribeGameModuleConfig.symbolWinAnimation.playStrokeAnimation(reelId, symbolId, fullSize), startTime);
        }
        // only play on the last reel top symbol.
        if((reelId === betWayWinData.length - 1) && betWayWinData.reelWinPositions[reelId].indexOf(true) === symbolId) {
            // The win number animation will not be removed by abort and stop.
            const winNumberTimeline:TimelineLite = this._gameView.playWinNumbers(betWayWinData, position);
            this._winNumberTimelines.push(winNumberTimeline);
            timeline.add(() => winNumberTimeline.play(), startTime);
            (<TimelineLite>timeline).removeLabel("displayed"); // To remove if that's not the last one.
            (<TimelineLite>timeline).addLabel("displayed", startTime);
        }
        return 0;
    }

    protected addWinCountUpToTimeline(timeline:Timeline):void {
        const gameData:DragonTribeParsedGameData = <DragonTribeParsedGameData>this._gameData!;
        if(gameData.isEndBigWin) {
            // leave the ways won move down and remove itself.
            return;
        }

        const totalWin:number = gameData.avalancheTotalWin;
        const from:number = 0;
        const duration:number = gameData.currentAvalancheWinRatioConfig!.duration;
        const countUpTimeline:TimelineLite = new TimelineLite();
        countUpTimeline.add([
            () => DragonTribeGameModuleConfig.soundPlayer.playNormalWinSound(gameData.currentAvalancheWinRatioConfig!),
            this._winCountUp.getCountUpAnimation(0, gameData.avalancheTotalWin, duration)
        ], "+=0.2");
        countUpTimeline.add(() => SlotGame.winFieldController.showWinField(this._gameData!.totalWin));

        timeline.add([
            () => {
                if(duration > 1) {
                    this._gameView.playBlueGems((duration > 1.5) ? 1 : 0);
                }
            },
            countUpTimeline
        ], "displayed+=0.1");
    }

    protected hasWin():boolean {
        const data: DragonTribeParsedGameData = (<DragonTribeParsedGameData>this._gameData!);

        // #315
        DragonTribeGameConfig.instance.WIN_BELOW_STAKE = (SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (data.singleWin <= data.playedBetValue));

        return data.avalancheTotalWin > 0;
    }

    protected startWinPresentation():void {
        const timeline:TimelineLite = new TimelineLite();
        this._animationTimeline = new TimelineLite({paused : true});
        if(this._betWinsData != null && this._betWinsData.length > 0) {
            this.addWinAnimationsToTimeline(this._animationTimeline);
        } else {
            this.addWinCountUpToTimeline(this._animationTimeline);
        }

        timeline.add(() => this._animationTimeline!.play());
        timeline.add(() => {
            this.finish();
        }, this._animationTimeline.totalDuration() + 0.1);
        this._timeline = timeline;
    }

    protected addWinAnimationsToTimeline(timeline:Timeline):void {
        let abortSoundPlayed:boolean = false;
        let startTime:number = 0.3;
        const allTogether:boolean = this._gameData!.currentWinRatioConfig!.duration < 1;
        for(let i:number = 0; i < this._betWinsData!.length; i++) {
            startTime += this.addSingleBetWinAnimationToTimeline(<IBetWayWinData>this._betWinsData![i], timeline, startTime);
        }
        (<DragonTribeParsedGameData>this._gameData!).allWinPositions.forEach((winPos:boolean[], reelId:number) => {
            let firstSymId:number = -1;
            winPos.forEach((hasWin:boolean, symId:number) => {
                if(hasWin) {
                    timeline.add([
                        () => {
                            if(firstSymId < 0 && !abortSoundPlayed) {
                                const soundId:number = (allTogether ? 0 : (reelId % 3 + 1));

                                // #315
                                if (!DragonTribeGameConfig.instance.WIN_BELOW_STAKE) {
                                    SlotGame.sound.playEffect(DragonTribeSoundConfig.SYMBOL_WIN_EXPLOSIONS[soundId]);
                                }

                                firstSymId = symId;
                                if(this._animationAborted) {
                                    abortSoundPlayed = true;
                                }
                            }
                        },
                        DragonTribeGameModuleConfig.symbolWinAnimation.playWinExplodeAnimation(reelId, symId, false),
                        Reels.getSymbol(reelId, symId)!.changeState({
                            state : GameConfig.instance.SYMBOL_STATES.win,
                            setStack : true,
                            fadeInDuration : 0.1
                        })
                    ], startTime + 0.5 + (allTogether ? 0 : (reelId * (0.3 - reelId * 0.01))));
                }
            });
        });

        const avalancheTime:number = timeline.totalDuration() - (allTogether ? 0.3 : 0.8);

        timeline.add(() => SlotGame.winFieldController.showWinField(this._gameData!.totalWin), avalancheTime);

        const avalancheTimeline:TimelineLite = new TimelineLite({paused : true});
        let avalancheDelay:number = 0;
        (<DragonTribeParsedGameData>this._gameData!).allWinPositions.forEach((winPos:boolean[], reelId:number) => {
            if(winPos.includes(true)) {
                const avalanchePositions:boolean[] = winPos.concat();
                const numOffset:number = 6 - (<DragonTribeParsedGameData>this._gameData!).actualReelSize[reelId];
                for(let i:number = 0; i < numOffset; i++) {
                    avalanchePositions[i] = true;
                }
                avalancheDelay = Reels.addReelAvalancheToTimeline(reelId, avalancheTimeline, avalancheDelay, reelId != 0, avalanchePositions);
            }
        });
        timeline.add(() => avalancheTimeline.play(), avalancheTime);
        timeline.add(() => {}, avalancheTime + avalancheTimeline.totalDuration() - 0.3);
    }

    protected stopWinPresentation(isAborted:boolean):void {
        if(this._timeline) {
            if(this._timeline.isActive()) {
                this._timeline.pause();
                this._timeline.kill();
            }
            this._timeline = null;
        }
        if(this._animationTimeline) {
            // Logger.logDev(`initial win animation timeline progress ${this._animationTimeline.progress()}`);
            this._animationAborted = true;
            if(this._animationTimeline.progress() === 0) {
                this._animationTimeline.play();
                this._animationTimeline.progress(1);
                this._animationTimeline.kill();
            } else if(this._animationTimeline.isActive()) {
                // Logger.logDev(`initial win animation timeline aborted!`);
                this._animationTimeline.progress(1);
                this._animationTimeline.kill();
            }
            this._animationTimeline = null;
        }
        // Do no abort the avalanche timeline so it can be continued.

        DragonTribeGameModuleConfig.symbolWinAnimation.resetAnimation();
        this._winSymbols = [];
        if(!(this._betWinsData != null && this._betWinsData.length > 0)) {
            this._winCountUp.abort((<DragonTribeParsedGameData>this._gameData!).avalancheTotalWin, isAborted, this._gameData!.isBigWin);
        } else {
            this._waysWon.abort(isAborted, false);
        }
    }

    public resetFeature():void {
        this._winWaysWon = 0;
        // Only abort/stop win numbers when skip/spinStart/ScreenStart
        while(this._winNumberTimelines.length > 0) {
            let timeline:TimelineLite | null = this._winNumberTimelines.pop()!;
            if(timeline.progress() === 0) {
                timeline.play();
                timeline.progress(1);
                timeline.kill();
            } else if(timeline.isActive()) {
                // Logger.logDev(`initial win animation timeline aborted!`);
                timeline.progress(1);
                timeline.kill();
            }
            timeline = null;
        }
        this._animationAborted = false;
    }

    protected findSymbol(reelId:number, symId:number):StateReelSymbol | null {
        const symbol = Reels.getSymbol(reelId, symId);
        if(symbol === null) {
            debugger;
            throw new Error(`Error: SequentiallyBetWayInitialWPController.playBetWinPresentation(): Cannot find symbol at reel ${reelId} symbol ${symId}`);
        }
        return symbol;
    }
}